import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, Plus, Trash2, ShieldCheck, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_RULES = 5;
const MAX_LEN = 300;

export default function FirmStyleGuardrailsTab() {
  const [rules, setRules] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/firm-style-guardrails`,
        { withCredentials: true });
      const loaded = res.data?.rules || [];
      setRules(loaded.length ? loaded : ['']);
      setMeta({ updated_at: res.data?.updated_at, updated_by: res.data?.updated_by });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load guardrails');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const updateRule = (idx, value) => {
    setRules(r => r.map((x, i) => (i === idx ? value.slice(0, MAX_LEN) : x)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) return;
    setRules(r => [...r, '']);
  };

  const removeRule = (idx) => {
    setRules(r => r.length === 1 ? [''] : r.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    const cleaned = rules.map(r => r.trim()).filter(Boolean).slice(0, MAX_RULES);
    try {
      const res = await axios.put(`${API_URL}/api/settings/firm-style-guardrails`,
        { rules: cleaned }, { withCredentials: true });
      setRules((res.data?.rules || []).length ? res.data.rules : ['']);
      toast.success(res.data?.message || 'Saved');
      fetchRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading firm guardrails…
      </div>
    );
  }

  const filledCount = rules.filter(r => r.trim()).length;

  return (
    <div className="space-y-4" data-testid="firm-guardrails-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[#0F2040] text-base">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Firm-wide AI Style Guardrails
          </CardTitle>
          <div className="text-[11px] text-slate-500 flex items-start gap-1.5 mt-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
            Up to {MAX_RULES} non-negotiable rules applied to every AI-drafted
            Change Request response. These override personal style preferences.
            Example: <em>"Never commit to a specific go-live date"</em>,
            <em> "Always reference the MSA by name"</em>.
          </div>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {rules.map((rule, idx) => (
            <div key={idx} className="flex items-start gap-2" data-testid={`guardrail-row-${idx}`}>
              <div className="mt-2 text-[11px] text-slate-400 font-mono w-4 shrink-0">
                {idx + 1}.
              </div>
              <Textarea
                value={rule}
                onChange={e => updateRule(idx, e.target.value)}
                maxLength={MAX_LEN}
                rows={2}
                placeholder={`Guardrail #${idx + 1} — e.g. "Always reference the signed Master Services Agreement."`}
                className="text-xs flex-1"
                data-testid={`guardrail-input-${idx}`}
              />
              <Button type="button" size="sm" variant="outline"
                onClick={() => removeRule(idx)}
                className="h-8 px-2 text-slate-500 hover:text-rose-600"
                data-testid={`guardrail-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <Button type="button" size="sm" variant="outline"
              onClick={addRule} disabled={rules.length >= MAX_RULES}
              className="h-7 text-[11px]"
              data-testid="guardrail-add-btn">
              <Plus className="w-3 h-3 mr-1" />
              Add rule{rules.length >= MAX_RULES ? ` (max ${MAX_RULES})` : ''}
            </Button>
            <div className="text-[10px] text-slate-400">
              {filledCount} / {MAX_RULES} filled
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-200">
            <div className="text-[10px] text-slate-400">
              {meta.updated_at
                ? <>Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || 'admin'}</>
                : <>Never saved</>}
            </div>
            <Button type="button" size="sm" onClick={save} disabled={saving}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-7 text-[11px]"
              data-testid="guardrail-save-btn">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save guardrails
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
